import { Component, effect, input, output } from '@angular/core';
import { ISelectMultipleOptionStatus } from 'src/app/project/components/input/select-multiple/select-multiple.model';
import { logEventInGTAG } from 'src/app/project/services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventQuickFilter,
} from 'src/app/project/services/analytics/google-analytics.consts';
import { EBasicField } from 'src/app/project/shared/enums/basic-fields-enums';
import { AccountService } from '../../account/account-service/account.service';
import { TAccount } from '../../account/account.model';
import { getAdvancedStatusValues } from '../../filters-advanced/advanced-filters-dropdown/advanced-filters-row/advanced-filter-types/advanced-filter-status/advanced-filter-status-utils';
import { TSearchFilter } from '../../filters-advanced/models/advanced-filter.model';
import { EPointSearchOperator } from '../../points/point-search/point-search.consts';
import { EPriority, TPriority } from '../../points/priorities';
import { TFilters } from '../site-filter.model';
import { defaultPriorities, getSyncConfigs } from './quick-filters.model';
import { EWorkspaces } from '../../workspace/workspaces.enum';
import { checkCustomWorkspaceId } from '../../workspace/workspace';

@Component({
  selector: 'pp-quick-filters',
  standalone: false,
  templateUrl: './quick-filters.component.html',
  styleUrl: './quick-filters.component.scss',
})
export class QuickFiltersComponent {
  ppFilters = input<TSearchFilter[]>();
  ppFiltersChange = output<TSearchFilter[]>();

  priorities: TPriority[] = defaultPriorities;
  allStatus: ISelectMultipleOptionStatus[] = getAdvancedStatusValues();
  userFilters: TFilters = this.defaultUserFilters;
  allAccounts: TAccount[];
  selectedFilters: TSearchFilter[] = [];
  cachedQuickFilters: Record<string, string[]> = {};

  isAllSelectedByKey = {
    priority: false,
    status: false,
  };
  selectedAccountSites: unknown[];
  isOverview = false;
  isTimeline = false;

  tagFilters: TFilters = this.defaultTagFilters;

  constructor(private accountService: AccountService) {
    effect(() => {
      const newFilters = this.ppFilters() ?? [];

      if (newFilters?.length > 0) {
        this.syncStateFromInput(newFilters);
      } else {
        this.resetAllInternalState();
      }

      this.isAllSelectedByKey.priority = this.isAllSelected('priority', this.priorities.length);
      this.isAllSelectedByKey.status = this.isAllSelected('status', this.allStatus.length);
    });

    this.isOverview = checkCustomWorkspaceId() === EWorkspaces.OVERVIEW;
    this.isTimeline = checkCustomWorkspaceId() === EWorkspaces.TIMELINE;
    this.allAccounts = this.accountService.getAccounts();
  }

  syncStateFromInput(newFilters: TSearchFilter[]): void {
    this.selectedFilters = newFilters.map((filter) => ({ ...filter }));
    const filterMap = new Map(this.selectedFilters.map((f) => [f.keys[0].toLowerCase(), f.values]));
    const syncConfigs = getSyncConfigs(this.priorities, this.allStatus, this.userFilters);

    for (const config of syncConfigs) {
      switch (config.type) {
        case 'toggle':
          this.handlePrefillToggles(filterMap, config);
          break;
        case 'user':
          this.handlePrefillMultiselect(filterMap, config, 'userFilters', this.userFilters);
          break;
        case 'tags':
          this.handlePrefillMultiselect(filterMap, config, 'tagFilters', this.tagFilters);
          break;
        case 'list': {
          const selectedValues = filterMap.get(config.key) || [];
          this.selectedAccountSites = selectedValues;
          break;
        }
      }
    }
  }

  handlePrefillToggles(filterMap: Map<string, unknown[]>, config): void {
    const toggleSelectedValues = filterMap.get(config.key.toLowerCase()) || [];

    config.items.forEach((item: TPriority | ISelectMultipleOptionStatus) => {
      item.selected = toggleSelectedValues.includes(item.value);
    });
  }

  handlePrefillMultiselect(
    filterMap: Map<string, unknown[]>,
    config,
    targetProp,
    targetState,
  ): void {
    const selectedValues = filterMap.get(config.key) || [];

    this[targetProp] = {
      ...targetState,
      [config.target]: {
        ...targetState[config.target],
        value: selectedValues,
      },
    };
  }

  resetAllInternalState(): void {
    this.selectedFilters = [];

    [this.priorities, this.allStatus].forEach((group) => {
      group.forEach((item) => {
        item.selected = false;
      });
    });

    this.userFilters = this.defaultUserFilters;
    this.tagFilters = this.defaultTagFilters;
    this.selectedAccountSites = [];
  }

  handleFilterSelection(key: string, value: string): void {
    const selectedKeyValue = this.selectedFilters.find((filter) => filter.keys[0] === key);

    if (selectedKeyValue) {
      const hasValueSelected = selectedKeyValue.values.includes(value);

      if (hasValueSelected) {
        selectedKeyValue.values = selectedKeyValue.values.filter(
          (selectedValue: EPriority) => selectedValue !== value,
        );

        if (selectedKeyValue.values.length === 0) {
          this.selectedFilters = this.selectedFilters.filter((filter) => filter.keys[0] !== key);
        }
      } else {
        selectedKeyValue.values = [...selectedKeyValue.values, value];
      }
    } else {
      this.selectedFilters.push({
        keys: [key],
        operator: EPointSearchOperator.IN,
        values: [value],
      });
    }

    this.ppFiltersChange.emit(this.selectedFilters);
  }

  triggerEventInGTAG(key: string): void {
    if (!key) {
      return;
    }

    const filterKey = key?.toUpperCase();

    switch (filterKey) {
      case 'PRIORITY':
        logEventInGTAG(EGoogleEventQuickFilter.PRIORITY, {
          source: EGoogleEventCategory.QUICK_FILTER,
        });
        break;
      case 'STATUS':
        logEventInGTAG(EGoogleEventQuickFilter.STATUS, {
          source: EGoogleEventCategory.QUICK_FILTER,
        });
        break;
      case 'CREATED BY':
        logEventInGTAG(EGoogleEventQuickFilter.CREATED_BY, {
          source: EGoogleEventCategory.QUICK_FILTER,
        });
        break;
      case 'ASSIGNEES':
        logEventInGTAG(EGoogleEventQuickFilter.ASSIGNED_TO, {
          source: EGoogleEventCategory.QUICK_FILTER,
        });
        break;
      case 'WORKSPACEREF.ID':
        logEventInGTAG(EGoogleEventQuickFilter.ACCOUNT_SITE, {
          source: EGoogleEventCategory.PROPOSED_QUICK_FILTER,
        });
    }
  }

  togglePriority(newPriority: TPriority): void {
    const currentPriority = this.priorities.find(
      (priority) => newPriority.value === priority.value,
    );
    currentPriority.selected = !currentPriority.selected;

    this.handleFilterSelection('priority', newPriority.value);
  }

  isAllSelected(key: string, optionLength: number): boolean {
    const currentFilter = this.selectedFilters.find((filter) => filter.keys[0] === key);

    return currentFilter?.values?.length === optionLength;
  }

  handleCreatedByOpen(): void {
    this.cachedQuickFilters = {
      ...this.cachedQuickFilters,
      [EBasicField.CREATED_BY]: [...this.userFilters.createdBy.value],
    };
  }

  handleAssignedToOpen(): void {
    this.cachedQuickFilters = {
      ...this.cachedQuickFilters,
      [EBasicField.ASSIGNEES]: [...this.userFilters.assignees.value],
    };
  }

  handleUpdateArrayFilterValues(
    key: string,
    values: string[],
    shouldTriggerGA?: boolean,
    operator?: EPointSearchOperator,
  ): void {
    const matchingFilter = this.selectedFilters.find((filter) => filter.keys[0] === key);

    if (values?.length) {
      if (matchingFilter) {
        matchingFilter.values = values;
      } else {
        this.selectedFilters = [
          ...this.selectedFilters,
          {
            keys: [key],
            operator: operator ?? EPointSearchOperator.IN,
            values: values,
          },
        ];
      }
    } else {
      this.selectedFilters = this.selectedFilters.filter((filter) => filter.keys[0] !== key);
    }

    if (shouldTriggerGA) {
      this.triggerEventInGTAG(key);
    }
    this.ppFiltersChange.emit(this.selectedFilters);
  }

  handleSelectAll(key: string, group: TPriority[] | ISelectMultipleOptionStatus[]): void {
    let values = [];

    if (group.length) {
      values = Object.values(group).map((value) => {
        value.selected = true;
        return value.value;
      });
    }

    this.handleUpdateArrayFilterValues(key, values, true);

    this.ppFiltersChange.emit(this.selectedFilters);
  }

  handleCloseTriggerGA(finalFilters: string[], key: string): void {
    const hasChanged =
      JSON.stringify(this.cachedQuickFilters[EBasicField[key]]) !== JSON.stringify(finalFilters);

    if (hasChanged && finalFilters.length > 0) {
      this.triggerEventInGTAG(EBasicField[key]);
    }
  }

  handleApplyCreatedByFilters(newFilters: TFilters['createdBy']['value']): void {
    this.userFilters.createdBy.value = [...newFilters];

    this.handleUpdateArrayFilterValues(EBasicField.CREATED_BY, newFilters);
  }

  handleAssignedToChange(newFilters: TFilters['assignees']['value']): void {
    this.userFilters.assignees.value = newFilters;
    this.handleUpdateArrayFilterValues(EBasicField.ASSIGNEES, newFilters);
  }

  handleDeselectAll(key: string, group: TPriority[] | ISelectMultipleOptionStatus[]): void {
    this.selectedFilters = this.selectedFilters.filter((filter) => filter.keys[0] !== key);

    group.forEach((value) => {
      value.selected = false;
    });

    this.isAllSelectedByKey[key] = false;

    this.ppFiltersChange.emit(this.selectedFilters);
  }

  updateStatusValue(newStatus: ISelectMultipleOptionStatus): void {
    this.handleFilterSelection('status', newStatus.value);

    const currentStatus = this.allStatus.find((status) => newStatus.value === status.value);
    currentStatus.selected = !currentStatus.selected;
  }

  applyTagFilters(): void {
    this.handleUpdateArrayFilterValues(EBasicField.TAGS, this.tagFilters.tags.value);
  }

  applyAccountSitesFilters(filters: string[]): void {
    this.handleUpdateArrayFilterValues('workspaceRef.id', filters ?? [], true);
  }

  applyExcludedTagFilters(): void {
    this.handleUpdateArrayFilterValues(
      EBasicField.EXCLUDED_TAGS,
      this.tagFilters.excludedTags.value,
      false,
      EPointSearchOperator.NOT_IN,
    );
  }

  private get defaultUserFilters(): TFilters {
    return {
      assignees: { value: [] },
      createdBy: { value: [] },
    };
  }

  private get defaultTagFilters(): TFilters {
    return {
      tags: { value: [], combinedTags: false },
      excludedTags: { value: [], combinedTags: false },
    };
  }
}
